import { createSlice } from '@reduxjs/toolkit';
import { AcsAtaPublicDetails, PotscastPublicDetails } from '../../types/device-details/ata.type';
import { EnclosurePublicDetails } from '../../types/device-details/enclosure.type';
import { EquipmentGroupPublicDetails } from '../../types/device-details/equipment-group.type';
import { RouterPublicDetails } from '../../types/device-details/router.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import snackbarUtils from '../../utils/SnackbarUtils';
import {
  getAcsAtaDeviceDetails,
  getDeviceOwnershipTransferLogs,
  getEnclosureDeviceDetails,
  getEquipmentGroupDeviceDetails,
  getPotscastDeviceDetails,
  getRouterDeviceDetails,
} from './deviceDetails.action';
import { OwnershipTransferLog } from '../../types/device-details/device-ownership-logs';

export interface DeviceDetailsState {
  enclosureDeviceDetails: EnclosurePublicDetails | null;
  enclosureDeviceDetailsFetchStatus: FETCH_STATUS;

  acsAtaDeviceDetails: AcsAtaPublicDetails | null;
  acsAtaDeviceDetailsFetchStatus: FETCH_STATUS;

  potscastDeviceDetails: PotscastPublicDetails | null;
  potscastDeviceDetailsFetchStatus: FETCH_STATUS;

  routerDeviceDetails: RouterPublicDetails | null;
  routerDeviceDetailsFetchStatus: FETCH_STATUS;

  equipmentGroupDeviceDetails: EquipmentGroupPublicDetails | null;
  equipmentGroupDeviceDetailsFetchStatus: FETCH_STATUS;

  ownershipTransferLogs: OwnershipTransferLog[];
  ownershipTransferLogsFetchStatus: FETCH_STATUS;
}

const INIT_STATE: DeviceDetailsState = {
  enclosureDeviceDetails: null,
  enclosureDeviceDetailsFetchStatus: FETCH_STATUS.NULL,

  acsAtaDeviceDetails: null,
  acsAtaDeviceDetailsFetchStatus: FETCH_STATUS.NULL,

  potscastDeviceDetails: null,
  potscastDeviceDetailsFetchStatus: FETCH_STATUS.NULL,

  routerDeviceDetails: null,
  routerDeviceDetailsFetchStatus: FETCH_STATUS.NULL,

  equipmentGroupDeviceDetails: null,
  equipmentGroupDeviceDetailsFetchStatus: FETCH_STATUS.NULL,

  ownershipTransferLogs: [],
  ownershipTransferLogsFetchStatus: FETCH_STATUS.NULL,
};

export const deviceDetailsSlice = createSlice({
  name: 'deviceDetails',
  initialState: INIT_STATE,
  reducers: {
    setOwnershipTransferLogsFetchStatus: (state, action) => {
      state.ownershipTransferLogsFetchStatus = action.payload;
    },
    clearOwnershipTransferLogs: state => {
      state.ownershipTransferLogs = [];
      state.ownershipTransferLogsFetchStatus = FETCH_STATUS.NULL;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getEnclosureDeviceDetails.pending, state => {
        state.enclosureDeviceDetailsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getEnclosureDeviceDetails.rejected, (state, action) => {
        state.enclosureDeviceDetailsFetchStatus = FETCH_STATUS.REJECTED;
        state.enclosureDeviceDetails = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      })
      .addCase(getEnclosureDeviceDetails.fulfilled, (state, action) => {
        state.enclosureDeviceDetailsFetchStatus = FETCH_STATUS.FULFILLED;
        state.enclosureDeviceDetails = action.payload;
      });

    builder
      .addCase(getAcsAtaDeviceDetails.pending, state => {
        state.acsAtaDeviceDetailsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getAcsAtaDeviceDetails.rejected, (state, action) => {
        state.acsAtaDeviceDetailsFetchStatus = FETCH_STATUS.REJECTED;
        state.acsAtaDeviceDetails = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      })
      .addCase(getAcsAtaDeviceDetails.fulfilled, (state, action) => {
        state.acsAtaDeviceDetailsFetchStatus = FETCH_STATUS.FULFILLED;
        state.acsAtaDeviceDetails = action.payload;
      });

    builder
      .addCase(getPotscastDeviceDetails.pending, state => {
        state.potscastDeviceDetailsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getPotscastDeviceDetails.rejected, (state, action) => {
        state.potscastDeviceDetailsFetchStatus = FETCH_STATUS.REJECTED;
        state.potscastDeviceDetails = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      })
      .addCase(getPotscastDeviceDetails.fulfilled, (state, action) => {
        state.potscastDeviceDetailsFetchStatus = FETCH_STATUS.FULFILLED;
        state.potscastDeviceDetails = action.payload;
      });

    builder
      .addCase(getRouterDeviceDetails.pending, state => {
        state.routerDeviceDetailsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getRouterDeviceDetails.rejected, (state, action) => {
        state.routerDeviceDetailsFetchStatus = FETCH_STATUS.REJECTED;
        state.routerDeviceDetails = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      })
      .addCase(getRouterDeviceDetails.fulfilled, (state, action) => {
        state.routerDeviceDetailsFetchStatus = FETCH_STATUS.FULFILLED;
        state.routerDeviceDetails = action.payload;
      });

    builder
      .addCase(getEquipmentGroupDeviceDetails.pending, state => {
        state.equipmentGroupDeviceDetailsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getEquipmentGroupDeviceDetails.rejected, (state, action) => {
        state.equipmentGroupDeviceDetailsFetchStatus = FETCH_STATUS.REJECTED;
        state.equipmentGroupDeviceDetails = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      })
      .addCase(getEquipmentGroupDeviceDetails.fulfilled, (state, action) => {
        state.equipmentGroupDeviceDetailsFetchStatus = FETCH_STATUS.FULFILLED;
        state.equipmentGroupDeviceDetails = action.payload;
      });

    builder
      .addCase(getDeviceOwnershipTransferLogs.pending, state => {
        state.ownershipTransferLogsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getDeviceOwnershipTransferLogs.rejected, (state, action) => {
        state.ownershipTransferLogsFetchStatus = FETCH_STATUS.REJECTED;
        state.ownershipTransferLogs = [];
      })
      .addCase(getDeviceOwnershipTransferLogs.fulfilled, (state, action) => {
        state.ownershipTransferLogsFetchStatus = FETCH_STATUS.FULFILLED;
        state.ownershipTransferLogs = action.payload;
      });
  },
});

export const { setOwnershipTransferLogsFetchStatus, clearOwnershipTransferLogs } = deviceDetailsSlice.actions;
export default deviceDetailsSlice.reducer;
